import { styles } from "./styles";
import type {
  AnalysisLiveEvent,
  AnalysisReport,
  AnalyzerEventName,
} from "./types";

const terminal = new Set(["completed", "partiallyCompleted", "failed"]);
const escapeHtml = (value: unknown) =>
  String(value ?? "").replace(
    /[&<>'"]/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[
        char
      ]!,
  );

export class SiteAnalyzerElement extends HTMLElement {
  private readonly root = this.attachShadow({ mode: "open" });
  private report?: AnalysisReport;
  private busy = false;
  private abort?: AbortController;
  private stream?: EventSource;
  private aiDraft = "";
  private livePriorities: Array<{
    findingId: string;
    title: string;
    whyItMatters: string;
    action: string;
    complete?: boolean;
  }> = [];
  private submittedUrl = "";
  private activeStrategy = "mobile";
  private activeResultTab = "overview";
  private contactModalOpen = false;
  private contactTimer?: number;
  private completedAt?: number;
  private contactTrigger: "automatic" | "floating" = "floating";
  private restoreFocus?: HTMLElement;
  private readonly contactSessionKey = "site-analyzer:contact-modal-shown:v2";
  private contactShownInMemory = false;

  connectedCallback() {
    this.style.setProperty(
      "--sa-primary",
      this.getAttribute("primary-color") || "#175cd3",
    );
    this.render();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.closeStream();
    if (this.contactTimer) window.clearTimeout(this.contactTimer);
  }
  private get apiUrl() {
    return (this.getAttribute("api-url") || "").replace(/\/$/, "");
  }

  private emit(name: AnalyzerEventName, detail: Record<string, unknown> = {}) {
    this.dispatchEvent(
      new CustomEvent(name, { detail, bubbles: true, composed: true }),
    );
  }

  private render() {
    this.root.innerHTML = `<style>${styles}</style><section class="shell" aria-label="INSYVEX — Analisador gratuito de sites com IA"${this.contactModalOpen ? " inert" : ""}>
      <header class="hero"><div class="product-mark"><strong>INSYVEX</strong><span>Analisador gratuito de sites com IA</span></div><h2>Faça uma análise gratuita do seu site</h2>
      <p>Informe o endereço do seu site para receber avaliações de desempenho, SEO, estrutura para buscas com IA e consentimento de cookies. O INSYVEX apresenta os resultados e utiliza inteligência artificial para ajudar você a interpretá-los.</p>
      <form class="start"><label class="sr-only" for="site-url">Endereço do site</label><input id="site-url" name="url" type="text" inputmode="url" autocomplete="url" autocapitalize="none" spellcheck="false" enterkeyhint="go" placeholder="seusite.com.br" value="${escapeHtml(this.submittedUrl || this.report?.url || "")}" required aria-describedby="privacy-note"><button type="submit" ${this.busy ? "disabled" : ""}>${this.busy ? "Analisando…" : "Analisar meu site gratuitamente"}</button></form>
      <small id="privacy-note" class="muted">A consulta e o relatório não exigem seus dados pessoais.</small></header>
      <div class="body" aria-live="polite">${this.bodyTemplate()}</div><footer class="product-attribution"><strong>Entenda seu site. Descubra oportunidades.</strong><span>INSYVEX é uma ferramenta desenvolvida pela Mundo do Software.</span></footer></section>${this.contactControlsTemplate()}`;
    this.root
      .querySelector<HTMLFormElement>("form.start")
      ?.addEventListener("submit", (event) => void this.start(event));
    this.root
      .querySelector<HTMLInputElement>("#site-url")
      ?.addEventListener("blur", (event) => {
        const input = event.currentTarget as HTMLInputElement;
        if (input.value.trim()) input.value = this.normalizeUrl(input.value);
      });
    this.root.querySelector<HTMLButtonElement>("[data-contact-open]")?.addEventListener("click", () => this.openContactModal("floating"));
    this.root.querySelectorAll<HTMLButtonElement>("[data-contact-close]").forEach((button) => button.addEventListener("click", () => this.closeContactModal()));
    this.root.querySelector<HTMLAnchorElement>("[data-whatsapp]")?.addEventListener("click", () => this.trackWhatsAppClick());
    this.root.querySelector<HTMLElement>("[data-contact-dialog]")?.addEventListener("keydown", (event) => this.handleDialogKeydown(event));
    this.root
      .querySelectorAll<HTMLButtonElement>("[data-strategy]")
      .forEach((button) =>
        button.addEventListener("click", () => {
          this.activeStrategy = button.dataset.strategy || "mobile";
          this.render();
        }),
      );
    this.root
      .querySelectorAll<HTMLButtonElement>("[data-result-tab]")
      .forEach((button) =>
        button.addEventListener("click", () => {
          this.activeResultTab = button.dataset.resultTab || "overview";
          this.render();
        }),
      );
    const scoreTooltip = this.root.querySelector<HTMLElement>(
      "[data-score-tooltip]",
    );
    this.root
      .querySelectorAll<HTMLElement>("[data-score-component]")
      .forEach((component) => {
        const show = () => {
          if (!scoreTooltip) return;
          scoreTooltip.querySelector("strong")!.textContent =
            `${component.dataset.label} · peso ${component.dataset.weight}%`;
          scoreTooltip.querySelector("span")!.textContent =
            component.dataset.measurement || "Métrica não disponível.";
          scoreTooltip.classList.add("visible");
        };
        const reset = () => {
          if (!scoreTooltip) return;
          scoreTooltip.querySelector("strong")!.textContent =
            "Composição da nota";
          scoreTooltip.querySelector("span")!.textContent =
            "Passe o mouse ou use Tab sobre uma métrica para ver seu peso.";
          scoreTooltip.classList.remove("visible");
        };
        component.addEventListener("mouseenter", show);
        component.addEventListener("focus", show);
        component.addEventListener("mouseleave", reset);
        component.addEventListener("blur", reset);
      });
    if (this.contactModalOpen) this.root.querySelector<HTMLButtonElement>("[data-contact-close]")?.focus();
  }

  private contactControlsTemplate() {
    if (!this.report || !terminal.has(this.report.status) || this.report.status === "failed") return "";
    const number = (this.getAttribute("whatsapp-number") || "").replace(/\D/g, "");
    const link = number ? `https://wa.me/${number}?text=${encodeURIComponent(this.whatsAppMessage())}` : "";
    const highImpact = this.report.recommendations.find((item) => item.impact === "high");
    const excellent = this.report.scores.some((score) => score.id === "lighthouse.performance" && score.value >= 90) && !highImpact;
    const contextual = excellent
      ? "Seu site apresentou ótimos resultados técnicos. Se você deseja evoluir sua presença digital ou melhorar a forma como apresenta seus serviços, podemos conversar sobre os próximos passos."
      : highImpact
        ? "Encontramos oportunidades relevantes para melhorar a experiência de quem acessa seu site. Podemos ajudar você a entender quais mudanças realmente valem a pena."
        : "Agora que você conhece melhor os pontos fortes e as oportunidades de melhoria do seu site, podemos ajudar a transformar essas recomendações em ações práticas.";
    const modal = this.contactModalOpen
      ? `<div class="contact-backdrop" data-contact-close aria-hidden="true"></div><section class="contact-modal" role="dialog" aria-modal="true" aria-labelledby="contact-title" data-contact-dialog><button class="contact-close" type="button" data-contact-close aria-label="Fechar convite">×</button><span class="section-kicker">Próximos passos</span><h2 id="contact-title">Quer ajuda para melhorar seu site?</h2><p>${escapeHtml(contextual)}</p><p>Identificou algo que gostaria de melhorar? A Mundo do Software pode ajudar você a entender quais ajustes fazem sentido para o seu site e para os objetivos da sua empresa.</p>${link ? `<a class="whatsapp-action" href="${link}" target="_blank" rel="noopener noreferrer" data-whatsapp>Conversar pelo WhatsApp</a>` : `<p class="contact-unavailable" role="status">O WhatsApp comercial não foi configurado neste componente.</p>`}<button class="contact-later" type="button" data-contact-close>Continuar vendo meu relatório</button></section>`
      : "";
    return `<button class="contact-floating" type="button" data-contact-open aria-haspopup="dialog">Falar sobre meu site</button>${modal}`;
  }

  private scheduleContactModal() {
    if (!this.report || this.report.status === "failed" || this.contactTimer || this.wasContactShown()) return;
    this.completedAt ??= Date.now();
    const configuredValue = this.getAttribute("contact-delay-ms");
    const configured = configuredValue === null ? Number.NaN : Number(configuredValue);
    const delay = Number.isFinite(configured) && configured >= 0 ? configured : 45_000;
    const openWhenClear = () => {
      this.contactTimer = undefined;
      if (!this.report || !terminal.has(this.report.status) || this.report.status === "failed" || this.wasContactShown()) return;
      if (document.querySelector('[aria-modal="true"],dialog[open]')) {
        this.contactTimer = window.setTimeout(openWhenClear, 5_000);
        return;
      }
      this.markContactShown();
      this.openContactModal("automatic");
    };
    this.contactTimer = window.setTimeout(openWhenClear, delay);
  }

  private openContactModal(trigger: "automatic" | "floating") {
    if (!this.report) return;
    this.restoreFocus = this.root.activeElement as HTMLElement | undefined;
    this.contactTrigger = trigger;
    this.contactModalOpen = true;
    this.markContactShown();
    const event = trigger === "automatic" ? "site_audit_contact_modal_shown" : "site_audit_contact_modal_reopened";
    if (trigger === "floating") this.emit("analyzer:cta-clicked", this.contactEventDetail(trigger));
    this.emit(event, this.contactEventDetail(trigger));
    this.render();
  }

  private closeContactModal() {
    if (!this.contactModalOpen) return;
    this.contactModalOpen = false;
    this.emit("site_audit_contact_modal_dismissed", this.contactEventDetail(this.contactTrigger));
    const target = this.restoreFocus;
    this.render();
    if (target?.isConnected) target.focus();
    else this.root.querySelector<HTMLButtonElement>("[data-contact-open]")?.focus();
  }

  private handleDialogKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") { event.preventDefault(); this.closeContactModal(); return; }
    if (event.key !== "Tab") return;
    const focusable = [...this.root.querySelectorAll<HTMLElement>("[data-contact-dialog] button,[data-contact-dialog] a[href]")];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && this.root.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && this.root.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  private trackWhatsAppClick() {
    this.emit("site_audit_whatsapp_clicked", this.contactEventDetail(this.contactTrigger));
  }

  private contactEventDetail(trigger: "automatic" | "floating") {
    let origin = "";
    try { origin = new URL(this.report?.url || "").origin; } catch { /* URL already validated by the API. */ }
    return { analysisId: this.report?.analysisId, origin, ctaLocation: "report", elapsedSinceCompletionMs: this.completedAt ? Date.now() - this.completedAt : 0, trigger };
  }

  private whatsAppMessage() {
    let analyzed = this.report?.url || "";
    try { const value = new URL(analyzed); value.search = ""; value.hash = ""; analyzed = value.toString(); } catch { analyzed = ""; }
    const finding = this.report?.recommendations.find((item) => item.impact === "high");
    const title = finding ? this.report?.findings.find((item) => item.id === finding.findingId)?.title : undefined;
    const issue = title ? `\n\nUm ponto relevante identificado: ${title.slice(0, 120)}.` : "";
    return `Olá! Utilizei o INSYVEX para analisar meu site e gostaria de conversar sobre as melhorias identificadas.\n\nSite analisado: ${analyzed}${issue}\n\nPodemos conversar?`.slice(0, 600);
  }

  private wasContactShown() { try { return this.contactShownInMemory || sessionStorage.getItem(this.contactSessionKey) === "1"; } catch { return this.contactShownInMemory; } }
  private markContactShown() { this.contactShownInMemory = true; try { sessionStorage.setItem(this.contactSessionKey, "1"); } catch { /* in-memory fallback */ } }

  private bodyTemplate() {
    if (!this.report)
      return `<div class="notice">A análise verifica a URL informada. Ela não representa automaticamente todas as páginas do site.</div>`;
    if (!terminal.has(this.report.status)) {
      const aiIsWriting = this.report.currentStep.startsWith(
        "A inteligência artificial",
      );
      const livePriorities = this.livePriorities.length
        ? `<section class="live-priorities" aria-live="off"><span class="draft-label"><i aria-hidden="true"></i> Prioridades sendo analisadas</span>${this.livePriorities.map((priority, index) => `<article><span class="priority-number">${index + 1}</span><div><h4>${escapeHtml(priority.title)}</h4><span class="live-field-label">Comentário da IA</span><p class="live-comment">${escapeHtml(priority.whyItMatters)}${!priority.complete && !priority.action ? '<span class="caret" aria-hidden="true"></span>' : ""}</p>${priority.action ? `<div class="priority-action"><strong>Ação sugerida:</strong> ${escapeHtml(priority.action)}${priority.complete ? "" : '<span class="caret" aria-hidden="true"></span>'}</div>` : ""}</div></article>`).join("")}</section>`
        : "";
      return `<div class="progress${aiIsWriting ? " ai-writing" : ""}" role="status"><span class="spinner" aria-hidden="true"></span><strong>${escapeHtml(this.report.currentStep)}${aiIsWriting ? '<span class="typing" aria-hidden="true"><i></i><i></i><i></i></span>' : ""}</strong></div>${this.aiDraft ? `<section class="ai-draft" aria-live="off" aria-label="Análise sendo gerada"><span class="draft-label"><i aria-hidden="true"></i> Análise em andamento</span><p>${escapeHtml(this.aiDraft)}<span class="caret" aria-hidden="true"></span></p></section>` : ""}${livePriorities}`;
    }
    if (this.report.status === "failed")
      return `<div class="notice error" role="alert"><strong>Não conseguimos concluir desta vez.</strong><br>${escapeHtml(this.report.currentStep)} Tente novamente em alguns instantes.</div>`;
    return this.resultsTemplate(this.report);
  }

  private resultsTemplate(report: AnalysisReport) {
    const runs = report.lighthouseRuns || [];
    const ownedScores = report.ownedScores || [];
    const activeRun =
      runs.find((run) => run.strategy === this.activeStrategy) || runs[0];
    const scoreData = activeRun?.scores || report.scores;
    const metricData = activeRun?.metrics || report.metrics;
    const renderScoreRing = (label: string, value: number) =>
      `<div class="score-ring" role="img" aria-label="${escapeHtml(label)}: ${value} de 100"><svg viewBox="0 0 44 44" aria-hidden="true"><circle class="score-ring-track" cx="22" cy="22" r="18" pathLength="100"></circle><circle class="score-ring-value" cx="22" cy="22" r="18" pathLength="100" stroke-dasharray="${value} ${100 - value}"></circle></svg><div><strong>${value}%</strong><span>nota</span></div></div>`;
    const renderScore = (s: AnalysisReport["scores"][number]) => {
        const value = Math.max(
          0,
          Math.min(100, Math.round(Number(s.value) || 0)),
        );
        const tone = value >= 90 ? "good" : value >= 50 ? "warn" : "bad";
        return `<article class="score-card ${tone}">${renderScoreRing(s.label, value)}<div class="score-copy"><h4>${escapeHtml(s.label)}</h4><p>${escapeHtml(s.source)}</p></div></article>`;
      };
    const performanceScore = scoreData.find(
      (score) => score.id === "lighthouse.performance",
    );
    const secondaryScores = scoreData.filter(
      (score) => score.id !== "lighthouse.performance",
    );
    const scores = secondaryScores.map(renderScore).join("");
    const metrics = metricData
      .map((metric) => {
        const formatted = this.formatMetric(metric);
        const rating = this.metricRating(metric.rating);
        return `<article class="metric-card ${rating.className}"><header><h4>${escapeHtml(metric.label)}</h4><span>${rating.label}</span></header><div class="metric-value">${escapeHtml(formatted.value)}${formatted.unit ? `<small>${escapeHtml(formatted.unit)}</small>` : ""}</div><p>${escapeHtml(metric.source)}</p></article>`;
      })
      .join("");
    const findingsById = new Map(
      report.findings.map((finding) => [finding.id, finding]),
    );
    const importantRecommendations = report.recommendations.filter((item) => item.impact === "high" || item.impact === "medium" || !item.impact);
    const optionalRecommendations = report.recommendations.filter((item) => item.impact === "optional");
    const recommendations = importantRecommendations
      .map((r) => {
        const finding = findingsById.get(r.findingId);
        return `<li><div><strong>${escapeHtml(finding?.title || "Melhoria recomendada")}</strong><span>${escapeHtml(r.action)}</span></div></li>`;
      })
      .join("");
    const findings = report.findings
      .map(
        (f) =>
          `<details><summary><span>${escapeHtml(f.title)}</span><span class="status ${escapeHtml(f.status)}">${this.statusLabel(f.status)}</span></summary><div class="detail"><p>${escapeHtml(f.explanation)}</p><p><strong>Próximo passo:</strong> ${escapeHtml(f.recommendation)}</p><details class="technical-details"><summary>Ver detalhes técnicos</summary><div class="technical"><strong>Fonte:</strong> ${escapeHtml(f.evidence.source)}${f.evidence.observedValue ? ` · ${escapeHtml(f.evidence.observedValue)} ${escapeHtml(f.evidence.unit)}` : ""}</div></details></div></details>`,
      )
      .join("");
    const limitations = report.limitations
      .map((l) => `<li>${escapeHtml(l)}</li>`)
      .join("");
    const aiLimitation = report.limitations.find((item) =>
      /\bIA\b|inteligência artificial/i.test(item),
    );
    const interpretation = report.aiModel
      ? `<div class="source-note ai"><strong>Inteligência artificial</strong><span>A IA explica os resultados encontrados, destaca pontos positivos e ajuda você a entender quais melhorias merecem atenção. Ela não altera notas nem inventa resultados.</span></div>`
      : `<div class="source-note unavailable"><strong>Análise por inteligência artificial indisponível</strong><span>${escapeHtml(aiLimitation || "Esta execução usou somente as verificações técnicas automáticas.")}</span></div>`;
    const performanceStatus = scoreData.length
      ? `<div class="source-note measured"><strong>PageSpeed Insights</strong><span>Os indicadores de velocidade, acessibilidade e boas práticas são obtidos dos testes oficiais do Google para celular e computador.</span></div>`
      : `<div class="source-note"><strong>PageSpeed/Lighthouse indisponível</strong><span>Esta execução não possui notas do Google; não compare o checklist de HTML com o PageSpeed Insights.</span></div>`;
    const ownedStatus = ownedScores.length
      ? `<div class="source-note owned"><strong>Avaliações próprias do INSYVEX</strong><span>O INSYVEX utiliza critérios próprios para verificar a estrutura de SEO, a preparação técnica para buscas com IA e aspectos do consentimento de cookies. Essas notas são separadas das notas do Lighthouse.</span></div>`
      : "";
    const strategySelector = runs.length
      ? `<section class="lab-report lab-toolbar"><header><div><span class="section-kicker">Teste de laboratório</span><h3>PageSpeed por dispositivo</h3></div><div class="strategy-tabs" role="tablist" aria-label="Dispositivo analisado">${runs.map((run) => `<button type="button" role="tab" data-strategy="${run.strategy}" aria-selected="${run.strategy === activeRun?.strategy}">${run.strategy === "mobile" ? "Celular" : "Computador"}</button>`).join("")}</div></header><p class="variance-note">Cada execução é uma amostra independente; horário, rede e servidor podem alterar o resultado.</p></section>`
      : "";
    const strategyCapture = runs.length
      ? activeRun?.screenshotDataUrl
        ? `<figure class="page-capture"><img src="${activeRun.screenshotDataUrl}" alt="Captura da página feita pelo Lighthouse na execução para ${escapeHtml(activeRun.label)}"><figcaption>Captura real desta execução — ${escapeHtml(activeRun.label)}${activeRun.fetchedAt ? ` · ${escapeHtml(new Date(activeRun.fetchedAt).toLocaleString("pt-BR"))}` : ""}${activeRun.lighthouseVersion ? ` · Lighthouse ${escapeHtml(activeRun.lighthouseVersion)}` : ""}</figcaption></figure>`
        : `<div class="capture-missing">O Google não devolveu uma captura para esta execução.</div>`
      : "";
    const scoreComponents = [
      { key: "fcp", label: "FCP", weight: 10, id: "lab.first-contentful-paint" },
      { key: "lcp", label: "LCP", weight: 25, id: "lab.largest-contentful-paint" },
      { key: "tbt", label: "TBT", weight: 30, id: "lab.total-blocking-time" },
      { key: "cls", label: "CLS", weight: 25, id: "lab.cumulative-layout-shift" },
      { key: "si", label: "SI", weight: 10, id: "lab.speed-index" },
    ];
    const metricScore = (metric?: AnalysisReport["metrics"][number]) =>
      Math.max(
        0,
        Math.min(
          100,
          Math.round(
            metric?.scorePercent ??
              (metric?.rating === "good"
                ? 100
                : metric?.rating === "needs-improvement"
                  ? 65
                  : metric?.rating === "poor"
                    ? 25
                    : 0),
          ),
        ),
      );
    let scoreOffset = 0;
    const scoreSegments = scoreComponents
      .map((component) => {
        const start = scoreOffset;
        scoreOffset += component.weight;
        const metric = metricData.find((item) => item.id === component.id);
        const formatted = metric ? this.formatMetric(metric) : undefined;
        const ratingClass = metric
          ? this.metricRating(metric.rating).className
          : "metric-neutral";
        const componentScore = metricScore(metric);
        const measurement = formatted
          ? `Pontuação da métrica: ${componentScore}%. Medição: ${formatted.value}${formatted.unit ? ` ${formatted.unit}` : ""}.`
          : "Métrica não disponível nesta execução.";
        const visibleWeight = component.weight - 1.8;
        const filledWeight = visibleWeight * (componentScore / 100);
        const attributes = `data-score-component data-label="${component.label}" data-weight="${component.weight}" data-measurement="${escapeHtml(measurement)}"`;
        return `<g class="${ratingClass}" ${attributes} tabindex="0" role="button" aria-label="${component.label}: peso ${component.weight}%. ${escapeHtml(measurement)}"><circle class="segment-track" pathLength="100" cx="100" cy="100" r="72" style="--segment-offset:-${start}" stroke-dasharray="${visibleWeight} ${100 - visibleWeight}"></circle><circle class="segment-value" pathLength="100" cx="100" cy="100" r="72" style="--segment-offset:-${start}" stroke-dasharray="${filledWeight} ${100 - filledWeight}"></circle></g>`;
      })
      .join("");
    const scoreLabels = scoreComponents
      .map((component) => {
        const metric = metricData.find((item) => item.id === component.id);
        const formatted = metric ? this.formatMetric(metric) : undefined;
        const componentScore = metricScore(metric);
        const measurement = formatted
          ? `Pontuação da métrica: ${componentScore}%. Medição: ${formatted.value}${formatted.unit ? ` ${formatted.unit}` : ""}.`
          : "Métrica não disponível nesta execução.";
        return `<button type="button" class="weight-label weight-${component.key}" data-score-component data-label="${component.label}" data-weight="${component.weight}" data-measurement="${escapeHtml(measurement)}">${component.label}</button>`;
      })
      .join("");
    const performanceOverview = performanceScore
      ? (() => {
          const value = Math.max(
            0,
            Math.min(100, Math.round(Number(performanceScore.value) || 0)),
          );
          const tone = value >= 90 ? "good" : value >= 50 ? "warn" : "bad";
          return `<section class="performance-overview"><div class="performance-score ${tone}"><span class="section-kicker">Nota geral</span><div class="weighted-gauge" role="group" aria-label="Desempenho: ${value} de 100"><svg class="score-progress" viewBox="0 0 100 100" aria-hidden="true"><circle class="score-progress-track" cx="50" cy="50" r="42" pathLength="100"></circle><circle class="score-progress-value" cx="50" cy="50" r="42" pathLength="100" stroke-dasharray="${value} ${100 - value}"></circle></svg><svg class="score-components" viewBox="0 0 200 200" aria-label="Composição ponderada da nota de desempenho">${scoreSegments}</svg>${scoreLabels}<div class="gauge-value"><strong>${value}</strong><span>de 100</span></div></div><h3>Desempenho</h3><p>Pontuação calculada pelo Lighthouse para ${escapeHtml((activeRun?.label || "celular").toLowerCase())}.</p><div class="weight-tooltip" data-score-tooltip><strong></strong><span></span></div><a class="score-method" href="https://developer.chrome.com/docs/lighthouse/performance/performance-scoring" target="_blank" rel="noopener noreferrer">Como a nota é calculada</a><div class="score-scale"><span class="bad">0–49</span><span class="warn">50–89</span><span class="good">90–100</span></div></div>${strategyCapture}</section>`;
        })()
      : strategyCapture;
    const assessment = report.aiAssessment;
    const contactFindings = report.findings.filter((item) => item.category === "contact" && item.status === "passed");
    const contactSummary = contactFindings.length
      ? `Foram identificados ${contactFindings.slice(0, 3).map((item) => item.title.toLowerCase()).join(", ")}. Confirme manualmente se esses caminhos estão visíveis e funcionam bem.`
      : "Não foi possível confirmar um caminho de contato no HTML recebido; vale revisar manualmente a página.";
    const finalAssessment = assessment
      ? `<section class="ai-assessment"><header><div><span class="ai-badge"><i aria-hidden="true"></i> Análise por inteligência artificial</span><h3>${escapeHtml(assessment.headline)}</h3></div></header><p class="assessment-description">${escapeHtml(assessment.description)}</p><div class="summary-grid">${assessment.strengths.length ? `<div class="strengths"><h4>Pontos fortes</h4><ul>${assessment.strengths.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul></div>` : ""}<div class="contact-summary"><h4>Facilidade de contato</h4><p>${escapeHtml(contactSummary)}</p></div><div class="next-steps"><h4>Próximos passos</h4><p>${importantRecommendations.length || assessment.priorities.length ? "Comece pelos itens de impacto alto ou médio apresentados na aba Prioridades." : "Não há uma correção importante identificada nesta execução; os refinamentos opcionais continuam disponíveis."}</p></div></div></section>`
      : `<section class="summary"><span class="eyebrow">Resumo do seu site</span><h3>${escapeHtml(report.summary?.headline)}</h3><p>${escapeHtml(report.summary?.description)}</p></section>`;
    const aiPriorities = assessment?.priorities.length
      ? `<section class="ai-priorities"><header class="section-heading"><div><span class="section-kicker">Leitura dos resultados</span><h3>O que merece atenção primeiro</h3></div><span>${assessment.priorities.length} prioridades</span></header>${assessment.priorities.map((priority, index) => `<article><span class="priority-number">${index + 1}</span><div><h4>${escapeHtml(priority.title)}</h4><p class="ai-comment"><strong>${priority.generatedByAi === false ? "Explicação automática" : "Comentário da IA"}</strong>${escapeHtml(priority.whyItMatters)}</p><div class="priority-action"><strong>Ação recomendada:</strong> ${escapeHtml(priority.action)}</div><small><strong>Evidência:</strong> ${escapeHtml(priority.evidence)}</small></div></article>`).join("")}</section>`
      : recommendations
        ? `<section><h3>O que merece atenção primeiro</h3><ol class="recommendations">${recommendations}</ol></section>`
        : `<div class="notice">Nenhuma correção de impacto alto ou médio foi identificada nesta execução.</div>`;
    const optionalItems = optionalRecommendations.map((item) => { const finding = findingsById.get(item.findingId); return `<li><strong>${escapeHtml(finding?.title || "Refinamento")}</strong><span>${escapeHtml(item.action)}</span></li>`; }).join("");
    const optionalSection = optionalItems ? `<details class="optional-improvements"><summary>Melhorias opcionais (${optionalRecommendations.length})</summary><ul>${optionalItems}</ul></details>` : "";
    const priorityCount = Math.max(importantRecommendations.length, assessment?.priorities.length || 0);
    const ownedScoreCards = ownedScores
      .map((score) => {
        const value = Math.max(0, Math.min(100, Math.round(score.value)));
        const tone = value >= 90 ? "good" : value >= 50 ? "warn" : "bad";
        const coverage = score.isComplete === false ? "Não foi possível verificar completamente." : `${value}% dos critérios técnicos avaliados foram atendidos.`;
        return `<article class="score-card ${tone}">${renderScoreRing(score.label, value)}<div class="score-copy"><h4>${escapeHtml(score.label)}</h4><p>${coverage}</p><small>${escapeHtml(score.description)}</small></div></article>`;
      })
      .join("");
    const ownedCriteria = ownedScores
      .map(
        (score) =>
          `<section class="criteria-group"><header><h3>${escapeHtml(score.label)}</h3><span>${score.value}%</span></header>${score.criteria.map((criterion) => `<article class="criterion"><div class="criterion-heading"><strong>${escapeHtml(criterion.label)}</strong><span>Peso ${criterion.weight}% · ${escapeHtml(criterion.status)}</span></div><div class="criterion-bar" aria-label="Atendimento do critério: ${criterion.score ?? 0}%"><i style="--criterion-score:${criterion.score ?? 0}"></i></div><small>${escapeHtml(criterion.evidence)}</small></article>`).join("")}</section>`,
      )
      .join("");
    const ownedPanel = ownedScores.length
      ? `<section class="owned-assessment"><header><div><span class="section-kicker">Critérios próprios do INSYVEX</span><h3>SEO técnico, estrutura para mecanismos de IA e consentimento</h3></div><p>Os percentuais mostram quantos critérios técnicos implementados no INSYVEX foram atendidos na URL analisada. Uma nota de 100% significa que todos os critérios daquela avaliação foram atendidos; não significa SEO perfeito, presença garantida em respostas de IA ou conformidade integral com a LGPD. Esta avaliação não substitui nem altera as notas do Google, e a ausência de tags conhecidas não prova que não exista medição.</p></header><div class="scores owned-scores">${ownedScoreCards}</div><div class="criteria-groups">${ownedCriteria}</div></section>`
      : "";
    const availableTabs = [
      ["overview", "Resumo"],
      ...(ownedScores.length ? [["search", "SEO e AEO"]] : []),
      ...(runs.length || scoreData.length || metricData.length
        ? [["pagespeed", "PageSpeed"]]
        : []),
      ...(priorityCount || optionalRecommendations.length
        ? [["priorities", `Prioridades (${priorityCount})`]]
        : []),
      ...(report.findings.length
        ? [["audit", `Auditoria (${report.findings.length})`]]
        : []),
    ];
    const effectiveResultTab = availableTabs.some(
      ([id]) => id === this.activeResultTab,
    )
      ? this.activeResultTab
      : "overview";
    const tabs = availableTabs
      .map(([id, label]) => `<button type="button" role="tab" id="result-tab-${id}" data-result-tab="${id}" aria-controls="result-panel-${id}" aria-selected="${effectiveResultTab === id}">${label}</button>`)
      .join("");
    const panel = (id: string, content: string) =>
      `<section class="result-panel" id="result-panel-${id}" role="tabpanel" aria-labelledby="result-tab-${id}"${effectiveResultTab === id ? "" : " hidden"}>${content}</section>`;
    const pageSpeedPanel =
      runs.length || scoreData.length || metricData.length
        ? panel("pagespeed", `${strategySelector}${performanceOverview}${secondaryScores.length ? `<div class="section-heading"><h3>Outras pontuações — ${escapeHtml(activeRun?.label || "Celular")}</h3></div><div class="scores">${scores}</div><p class="score-caveat">Uma nota 100 em acessibilidade indica que os testes automáticos disponíveis passaram; ela não garante acessibilidade completa nem substitui testes com pessoas e tecnologias assistivas.</p>` : ""}${metricData.length ? `<div class="section-heading"><h3>Métricas de laboratório — ${escapeHtml(activeRun?.label || "Celular")}</h3></div><div class="metrics">${metrics}</div>` : ""}`)
        : "";
    const prioritiesPanel = priorityCount
      ? panel("priorities", `${aiPriorities}${optionalSection}`)
      : optionalSection ? panel("priorities", optionalSection) : "";
    const auditPanel = report.findings.length
      ? panel("audit", `<div class="section-heading"><div><span class="section-kicker">Evidências</span><h3>Detalhes da análise</h3></div></div><div class="audit-list">${findings}</div><details class="limitations"><summary>Limites desta análise</summary><ul>${limitations}</ul></details>`)
      : "";
    return `<div class="analyzed-url"><span>URL analisada</span><a href="${escapeHtml(report.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(report.url)}</a></div><nav class="result-tabs" role="tablist" aria-label="Seções do relatório">${tabs}</nav>
      ${panel("overview", `${finalAssessment}<div class="sources">${interpretation}${ownedStatus}${performanceStatus}</div>`)}
      ${ownedScores.length ? panel("search", ownedPanel) : ""}${pageSpeedPanel}${prioritiesPanel}${auditPanel}`;
  }

  private formatMetric(metric: AnalysisReport["metrics"][number]) {
    const paintMetric =
      metric.unit.toLowerCase() === "ms" &&
      /(largest-contentful-paint|first-contentful-paint|speed-index|(^|\.)lcp$|(^|\.)fcp$|(^|\.)si$)/i.test(
        metric.id,
      );
    if (paintMetric)
      return {
        value: (Number(metric.value) / 1000).toLocaleString("pt-BR", {
          minimumFractionDigits: 1,
          maximumFractionDigits: 2,
        }),
        unit: "s",
      };
    return {
      value: Number(metric.value).toLocaleString("pt-BR", {
        maximumFractionDigits: 3,
      }),
      unit: metric.unit,
    };
  }

  private metricRating(rating?: string) {
    const normalized = String(rating || "").toLowerCase();
    if (normalized === "good")
      return { className: "metric-good", label: "Bom" };
    if (normalized === "poor")
      return { className: "metric-poor", label: "Ruim" };
    if (normalized === "needs-improvement")
      return { className: "metric-warning", label: "Melhorar" };
    return { className: "metric-neutral", label: "Informativo" };
  }

  private async start(event: SubmitEvent) {
    event.preventDefault();
    if (!this.apiUrl) {
      this.showLocalError("O endereço da API não foi configurado.");
      return;
    }
    const form = event.currentTarget as HTMLFormElement;
    const input = form.elements.namedItem("url") as HTMLInputElement;
    const url = this.normalizeUrl(input.value);
    input.value = url;
    this.submittedUrl = url;
    this.busy = true;
    this.aiDraft = "";
    this.livePriorities = [];
    this.activeResultTab = "overview";
    this.activeStrategy = "mobile";
    this.contactModalOpen = false;
    this.completedAt = undefined;
    if (this.contactTimer) window.clearTimeout(this.contactTimer);
    this.contactTimer = undefined;
    this.closeStream();
    this.render();
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      const response = await fetch(`${this.apiUrl}/api/v1/analyses`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url }),
        signal: this.abort.signal,
      });
      if (!response.ok) throw new Error(await this.problemMessage(response));
      this.report = (await response.json()) as AnalysisReport;
      this.submittedUrl = this.report.url || url;
      this.emit("analyzer:started", { analysisId: this.report.analysisId });
      this.render();
      if (terminal.has(this.report.status)) {
        this.busy = false;
        this.render();
        this.scheduleContactModal();
        return;
      }
      this.listenStream(this.report.analysisId);
      await this.poll(this.report.analysisId, this.abort.signal);
    } catch (error) {
      if ((error as Error).name !== "AbortError")
        this.fail((error as Error).message);
    }
  }

  private async poll(id: string, signal: AbortSignal) {
    while (!signal.aborted) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      const response = await fetch(
        `${this.apiUrl}/api/v1/analyses/${encodeURIComponent(id)}`,
        { signal },
      );
      if (!response.ok)
        throw new Error("Não foi possível acompanhar a análise.");
      this.report = (await response.json()) as AnalysisReport;
      this.submittedUrl = this.report.url || this.submittedUrl;
      this.emit("analyzer:progress", {
        analysisId: id,
        status: this.report.status,
        step: this.report.currentStep,
      });
      this.render();
      if (terminal.has(this.report.status)) {
        this.busy = false;
        this.aiDraft = "";
        this.closeStream();
        this.emit(
          this.report.status === "failed"
            ? "analyzer:failed"
            : "analyzer:completed",
          { analysisId: id, status: this.report.status },
        );
        this.render();
        if (this.report.status !== "failed") this.scheduleContactModal();
        return;
      }
    }
  }

  private listenStream(id: string) {
    if (typeof EventSource === "undefined") return;
    this.closeStream();
    const stream = new EventSource(
      `${this.apiUrl}/api/v1/analyses/${encodeURIComponent(id)}/stream`,
    );
    this.stream = stream;
    const read = (event: MessageEvent<string>) => {
      try {
        return JSON.parse(event.data) as AnalysisLiveEvent;
      } catch {
        return undefined;
      }
    };
    for (const name of ["snapshot", "progress"] as const) {
      stream.addEventListener(name, (event) => {
        const update = read(event as MessageEvent<string>);
        if (!update || !this.report) return;
        if (update.step) this.report.currentStep = update.step;
        if (update.status) this.report.status = update.status;
        this.render();
      });
    }
    stream.addEventListener("ai.reset", () => {
      this.aiDraft = "";
      this.livePriorities = [];
      this.render();
    });
    stream.addEventListener("ai.delta", (event) => {
      const update = read(event as MessageEvent<string>);
      if (!update?.text) return;
      this.aiDraft += update.text;
      this.render();
    });
    const updatePriority = (event: Event, complete: boolean) => {
      const update = read(event as MessageEvent<string>);
      if (!update?.findingId || !update.title) return;
      const existing = this.livePriorities.findIndex(
        (item) => item.findingId === update.findingId,
      );
      const priority = {
        findingId: update.findingId,
        title: update.title,
        whyItMatters:
          update.whyItMatters || this.livePriorities[existing]?.whyItMatters || "",
        action: update.action || this.livePriorities[existing]?.action || "",
        complete,
      };
      if (existing >= 0) this.livePriorities[existing] = priority;
      else this.livePriorities.push(priority);
      this.render();
    };
    stream.addEventListener("ai.priority.delta", (event) =>
      updatePriority(event, false),
    );
    stream.addEventListener("ai.priority", (event) =>
      updatePriority(event, true),
    );
    for (const name of ["completed", "failed"] as const)
      stream.addEventListener(name, () => this.closeStream());
    stream.onerror = () => this.closeStream();
  }

  private closeStream() {
    this.stream?.close();
    this.stream = undefined;
  }

  private async problemMessage(response: Response) {
    try {
      const data = (await response.json()) as { title?: string };
      return data.title || "Não foi possível iniciar a análise.";
    } catch {
      return "Não foi possível iniciar a análise.";
    }
  }
  private normalizeUrl(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    try {
      const parsed = new URL(candidate);
      return parsed.protocol === "http:" || parsed.protocol === "https:"
        ? parsed.toString()
        : candidate;
    } catch {
      return candidate;
    }
  }
  private fail(message: string) {
    this.busy = false;
    this.report = undefined;
    this.render();
    this.showLocalError(message);
    this.emit("analyzer:failed", { reason: "request_failed" });
  }
  private showLocalError(message: string) {
    const body = this.root.querySelector<HTMLElement>(".body");
    if (body)
      body.innerHTML = `<div class="notice error" role="alert">${escapeHtml(message)}</div>`;
  }
  private statusLabel(status: string) {
    return (
      (
        {
          passed: "Tudo certo",
          confirmedProblem: "Corrigir",
          attention: "Atenção",
          notIdentified: "Não identificado",
          notAnalyzed: "Não analisado",
          checkFailed: "Falha na verificação",
        } as Record<string, string>
      )[status] || status
    );
  }
}
